import { useState, useEffect } from 'react';
import { rateLimiters, getAllRateLimiterStats, resetAllRateLimiters } from '../utils/rateLimiter';
import { POLLING_INTERVALS, getAllFetchStatus } from '../utils/smartPolling';
import { getDailyUsageProjections } from '../utils/intervalCalculator';
import { useTranslation } from '../hooks/useTranslation';

/**
 * Comprehensive Settings Panel with API Rate Limits
 */
export default function SettingsPanel({ isOpen, onClose, preferences, updatePreference }) {
  const { t } = useTranslation();
  const [rateLimitStats, setRateLimitStats] = useState([]);
  const [fetchStatus, setFetchStatus] = useState([]);
  const [dailyProjections, setDailyProjections] = useState({});
  const [activeTab, setActiveTab] = useState('general');

  // Update stats periodically
  useEffect(() => {
    if (!isOpen) return;

    const updateStats = () => {
      setRateLimitStats(getAllRateLimiterStats());
      setFetchStatus(getAllFetchStatus());
      setDailyProjections(getDailyUsageProjections(POLLING_INTERVALS));
    };

    updateStats();
    const interval = setInterval(updateStats, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [isOpen]);

  const handleResetRateLimits = () => {
    if (confirm('Reset all API rate limit counters? This will allow immediate API calls.')) {
      resetAllRateLimiters();
      setRateLimitStats(getAllRateLimiterStats());
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h`;
  };

  const getStatusColor = (percentUsed) => {
    if (percentUsed >= 90) return 'text-red-600';
    if (percentUsed >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusBg = (percentUsed) => {
    if (percentUsed >= 90) return 'bg-red-100';
    if (percentUsed >= 70) return 'bg-yellow-100';
    return 'bg-green-100';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('settings.title')}</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {[
            { id: 'general', label: t('settings.general'), icon: '⚙️' },
            { id: 'rate-limits', label: t('settings.rateLimits'), icon: '🚦' },
            { id: 'polling', label: t('settings.polling'), icon: '⏱️' },
            { id: 'data', label: t('settings.data'), icon: '💾' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('settings.refreshInterval')}
                </label>
                <input
                  type="number"
                  value={preferences.refreshInterval / 1000}
                  onChange={(e) => updatePreference('refreshInterval', parseInt(e.target.value) * 1000)}
                  min="30"
                  max="600"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('settings.refreshIntervalDesc')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('settings.stalenessThreshold')}
                </label>
                <input
                  type="number"
                  value={preferences.stalenessThreshold || 5}
                  onChange={(e) => updatePreference('stalenessThreshold', parseInt(e.target.value))}
                  min="1"
                  max="60"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('settings.stalenessThresholdDesc')}
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">{t('settings.optimizationTips')}</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>{t('settings.tip1')}</li>
                  <li>{t('settings.tip2')}</li>
                  <li>{t('settings.tip3')}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Rate Limits */}
          {activeTab === 'rate-limits' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('rateLimits.title')}</h3>
                <button
                  onClick={handleResetRateLimits}
                  className="px-3 py-1 text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-md transition-colors"
                >
                  {t('settings.resetAll')}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {rateLimitStats.map(stat => (
                  <div key={stat.name} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-900 dark:text-white">{stat.name}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBg(stat.daily.percentUsed)} ${getStatusColor(stat.daily.percentUsed)}`}>
                        {stat.daily.percentUsed.toFixed(1)}% {t('settings.used')}
                      </span>
                    </div>

                    {/* Daily Usage */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                        <span>{t('settings.dailyUsage')}</span>
                        <span>{stat.daily.used} / {stat.daily.limit}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all ${
                            stat.daily.percentUsed >= 90 ? 'bg-red-500' :
                            stat.daily.percentUsed >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(stat.daily.percentUsed, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('settings.resetsIn')} {formatTime(stat.daily.resetsIn * 60)}
                      </p>
                    </div>

                    {/* Per-minute Usage */}
                    <div>
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                        <span>{t('settings.perMinute')}</span>
                        <span>{stat.minute.used} / {stat.minute.limit}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-blue-500 transition-all"
                          style={{ width: `${Math.min((stat.minute.used / stat.minute.limit) * 100, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Resets in {formatTime(stat.minute.resetsIn)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">{t('settings.warnings')}</h4>
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {t('settings.warningDesc')}
                </p>
              </div>
            </div>
          )}

          {/* Polling Settings */}
          {activeTab === 'polling' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.pollingIntervals')}</h3>
              
              <div className="space-y-4">
                {fetchStatus.map(status => (
                  <div key={status.api} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white capitalize">{status.api}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        status.canFetch ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {status.canFetch ? t('settings.ready') : t('settings.waiting')}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <p>{t('settings.interval')} {status.interval} {t('settings.minutes')}</p>
                      <p>{t('settings.nextFetch')} {status.nextFetchIn > 0 ? `${t('settings.in')} ${formatTime(status.nextFetchIn)}` : t('settings.availableNow')}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Daily Usage Projections */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3">📊 {t('settings.dailyProjections')}</h4>
                <div className="space-y-2">
                  {Object.entries(dailyProjections).map(([apiName, projection]) => (
                    <div key={apiName} className="flex justify-between items-center text-sm">
                      <span className="capitalize font-medium">{apiName}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 dark:text-gray-400">
                          {projection.callsPerDay} / {projection.dailyLimit}
                        </span>
                        <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              projection.percentageUsed > 50 ? 'bg-red-500' :
                              projection.percentageUsed > 25 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(projection.percentageUsed, 100)}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${
                          projection.percentageUsed > 50 ? 'text-red-600' :
                          projection.percentageUsed > 25 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {projection.percentageUsed.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-blue-800 dark:text-blue-200 mt-2">
                  {t('settings.projectionDesc')}
                </p>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">✅ {t('settings.dailyLimitPolling')}</h4>
                <p className="text-sm text-green-800 dark:text-green-200">
                  {t('settings.dailyLimitDesc')}
                </p>
              </div>
            </div>
          )}

          {/* Data Management */}
          {activeTab === 'data' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.data')}</h3>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">{t('settings.historicalData')}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('settings.historicalDesc')}
                </p>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('settings.utilizationHistory')}</span>
                    <span className="text-gray-500">{t('settings.storedLocally')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t('settings.priceHistory')}</span>
                    <span className="text-gray-500">{t('settings.storedLocally')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t('settings.tvlHistory')}</span>
                    <span className="text-gray-500">{t('settings.storedLocally')}</span>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h4 className="font-medium text-red-900 dark:text-red-100 mb-2">🗑️ {t('settings.clearAllData')}</h4>
                <p className="text-sm text-red-800 dark:text-red-200 mb-3">
                  {t('settings.clearWarning')}
                </p>
                <button
                  onClick={() => {
                    if (confirm('Are you sure? This will delete all data and cannot be undone.')) {
                      localStorage.clear();
                      window.location.reload();
                    }
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                >
                  {t('settings.clearButton')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
